import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { useTable } from 'react-table';
import { Pagination, proofActions } from '../../../components';
import { utilService } from '../../../_helpers';
import { tenantService } from '../../Tenant';
import SearchComponent from '../SearchComponent/SearchComponent';
import PulseLoader from '../Loaders/PulseLoader';

import '../Grid/Grid.css';

const defaultColumnProperties = {
    sortable: false,
    filterable: false,
    resizable: true
};

/**
 * Inner functional component using react-table v7 useTable hook.
 * Renders the tree grid with expand/collapse icons in the Name column.
 */
function ListSelectorTable({ flatRows, columns, expandedRows, onCellClick }) {
    // Map columns to react-table format, with custom Cell renderer for the Name column
    const rtColumns = useMemo(() => {
        return columns.map(col => {
            const rtCol = {
                accessor: col.key,
                Header: col.name || '',
                ...(col.width ? { width: col.width } : {}),
            };

            // Custom cell renderer for the 'name' column with tree expand/collapse
            if (col.key === 'name') {
                rtCol.Cell = ({ row }) => {
                    const rowData = row.original;
                    const hasChildren = rowData.versions && rowData.versions.length > 0;
                    const indent = (rowData.treeDepth || 0) * 20;
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', paddingLeft: indent }}>
                            {hasChildren && (
                                <span
                                    className="rt-cell-expand"
                                    role="button"
                                    data-testid="tree-expand"
                                    style={{
                                        cursor: 'pointer',
                                        marginRight: 4,
                                        userSelect: 'none',
                                        display: 'inline-block',
                                        width: 16,
                                        textAlign: 'center',
                                    }}
                                >
                                    {rowData._expanded ? '\u25BC' : '\u25B6'}
                                </span>
                            )}
                            {!hasChildren && rowData.treeDepth > 0 && (
                                <span style={{ display: 'inline-block', width: 20 }} />
                            )}
                            <span>{rowData.name}</span>
                        </div>
                    );
                };
            }

            return rtCol;
        });
    }, [columns]);

    const data = useMemo(() => flatRows || [], [flatRows]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows: tableRows,
        prepareRow,
    } = useTable({
        columns: rtColumns,
        data,
    });

    return (
        <div className="rt-grid" style={{ height: 389, overflow: 'auto' }}>
            <table {...getTableProps()} className="rt-table">
                <thead>
                    {headerGroups.map(headerGroup => {
                        const { key: hgKey, ...hgProps } = headerGroup.getHeaderGroupProps();
                        return (
                            <tr key={hgKey} {...hgProps} className="rt-header-row">
                                {headerGroup.headers.map(column => {
                                    const { key: colKey, ...colProps } = column.getHeaderProps();
                                    return (
                                        <th
                                            key={colKey}
                                            {...colProps}
                                            className="rt-header-cell"
                                            style={column.width ? { width: column.width, minWidth: column.width } : {}}
                                        >
                                            {column.render('Header')}
                                        </th>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {tableRows.map(row => {
                        prepareRow(row);
                        const { key: rowKey, ...rowProps } = row.getRowProps();
                        return (
                            <tr
                                key={rowKey}
                                {...rowProps}
                                className={`rt-row${row.original.treeDepth > 0 ? ' rt-row--child' : ''}`}
                                onClick={(e) => onCellClick(row.original, e)}
                            >
                                {row.cells.map(cell => {
                                    const { key: cellKey, ...cellProps } = cell.getCellProps();
                                    return (
                                        <td
                                            key={cellKey}
                                            {...cellProps}
                                            className="rt-cell"
                                            style={cell.column.width ? { width: cell.column.width, minWidth: cell.column.width } : {}}
                                        >
                                            {cell.render('Cell')}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

const columns = [
    {
        key: 'name',
        name: 'Name',
        width: 250,
    },
    {
        key: 'versionNumber',
        name: 'Version'
    },
    {
        key: 'createdAt',
        name: 'Created'
    },
    {
        key: 'createdBy',
        name: 'Owner'
    }
].map((c) => ({ ...c, ...defaultColumnProperties }));

const ROW_COUNT = 50;

const groupBy = ['jobType'];

class ListSelector extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            expandedRows: {},
            rows: [],
            flatRows: [],
            isLoading: true,
            pageSize: 10,
            pageIndex: 0,
            totalPages: 0,
            totalCount: 0,
            proofFilters: []
        };
    }

    async componentDidMount() {
        await this.loadProofVersionsList();
    }

    async loadProofVersionsList(passDefaultId = true) {
        this.setState({ isLoading: true });
        let proofSettings = await tenantService.getProofSettings();    
        let proofFilters = [];
        
        if (proofSettings.comparisonType == 1){
            proofFilters = [{
                FilterColumn : "directoryNodeId",
                FilterValue : this.props.currentDirectoryNodeId
            }];
        }        
        else if (proofSettings.comparisonType == 2){
            proofFilters = [{
                FilterColumn : "proofId",
                FilterValue : this.props.proofId
            }];
        }
                            
        var requestObject = {
            page: this.state.pageIndex,
            pageSize: this.state.pageSize,
            proofSortModel: {
                sortDirection: -1,
                sortColumn: 'createdAt'
            },
            proofFilters: [...proofFilters, ...this.state.proofFilters],
        };

        if (passDefaultId && this.props.defaultSelectedId) {
            requestObject.defaultSelectedProofId = this.props.defaultSelectedProofId;
        }

        var proofVersionsResult = await this.props.dispatch(
            proofActions.getAllProofsVersions(requestObject)
        );
        const { list, pageIndex, pageSize, totalCount, totalPages } = proofVersionsResult;
        await utilService.setStateAsync.bind(this)({
            rows: list,
            flatRows: this.buildFlatRows(list, {}),
            isLoading: false,
            pageIndex,
            pageSize,
            totalCount,
            totalPages,
            expandedRows: {}
        });
                        
        if(this.props.defaultSelectedId){                       
            var selected;

            for(let i = 0; i< list.length; i++){
                
                if(list[i].id ===  this.props.defaultSelectedId)
                {
                    selected = list[i];
                    break;
                }

                var versions = list[i].versions;
                if (versions){
                    for (let j = 0;  j< versions.length; j++ ){
                        
                        if(versions[j].id ===  this.props.defaultSelectedId)
                        {                            
                            selected = versions[j];
                            break;
                        }
                    }
                }
            }
            
            if(selected){
                this.setState({ selectedRow: selected }, () => {
                    this.props.onRowSelected(selected);
                });
            }
        }
    }

    /**
     * Build a flat array of rows from the hierarchical data,
     * splicing in expanded children.
     */
    buildFlatRows(rows, expandedRows) {
        const flat = [];
        for (const row of rows) {
            const rowCopy = { ...row, treeDepth: 0, _expanded: !!expandedRows[row.id] };
            flat.push(rowCopy);
            if (expandedRows[row.id] && row.versions && row.versions.length > 0) {
                row.versions.forEach((child, i) => {
                    flat.push({
                        ...child,
                        treeDepth: 1,
                        siblingIndex: i,
                        numberSiblings: row.versions.length,
                        _expanded: false
                    });
                });
            }
        }
        return flat;
    }

    onCellClick(row, event) {
        if (!row) return;

        // Check if the click is on the expand icon
        const target = event?.target;
        const isExpandClick = target &&
            (target.classList?.contains('rt-cell-expand') ||
             target.closest?.('.rt-cell-expand') ||
             target.getAttribute?.('data-testid') === 'tree-expand');

        if (isExpandClick && row.versions && row.versions.length > 0) {
            // Toggle expansion
            const expandedRows = { ...this.state.expandedRows };
            expandedRows[row.id] = !expandedRows[row.id];
            const flatRows = this.buildFlatRows(this.state.rows, expandedRows);
            this.setState({ expandedRows, flatRows });
            return;
        }

        // Regular row click — select the row
        this.onRowSelected(row);
    }

    onRowSelected(row) {
        if (!row) return;

        var selectedRow = this.state.rows.find((item) => item.id == row.id);
        // If not found in top-level rows, look in children
        if (!selectedRow) {
            for (const parentRow of this.state.rows) {
                if (parentRow.versions) {
                    selectedRow = parentRow.versions.find((v) => v.id == row.id);
                    if (selectedRow) break;
                }
            }
        }
        if (selectedRow) {
            this.setState({ selectedRow: selectedRow }, () => {
                this.props.onRowSelected(selectedRow);
            });
        }
    }

    async handlePageIndexChange(pageIndex) {
        await utilService.setStateAsync.bind(this)({ pageIndex: pageIndex });
        await this.loadProofVersionsList(false);
    }

    pageChanged(data) {
        const page = data.selected;
        this.setState({ pageIndex: page });
        if (this.handlePageIndexChange) {
            this.handlePageIndexChange(page);
        }
    }

    async handleSearchClick(value) {
        await utilService.setStateAsync.bind(this)({
            pageIndex: 0,
            proofFilters: value ? [{ filterColumn: 'documentName', filterValue: value }] : []
        });
        await this.loadProofVersionsList(false);
    }

    async handleSearchClearClick() {
        await utilService.setStateAsync.bind(this)({
            pageIndex: 0,
            proofFilters: []
        });
        await this.loadProofVersionsList(false);
    }

    render() {
        return (
            <div>
                <div className="d-flex justify-content-between">
                    <SearchComponent
                        clearEnabled={true}
                        handleSearch={this.handleSearchClick.bind(this)}
                        handleCleared={this.handleSearchClearClick.bind(this)}
                        className="list-selector-search"
                    />
                    <Pagination
                        totalPages={this.state.totalPages}
                        pageChanged={this.pageChanged.bind(this)}
                        pageSizeChanged={() => {}}
                        selectedPage={this.state.pageIndex}
                        totalCount={this.state.totalCount}
                        showCountText={false}
                        showPageSizeChanger={false}
                    />
                </div>
                {this.state.flatRows.length === 0 && this.state.isLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 389 }}>
                        <PulseLoader backgroundColor="var(--secondary-text-color)" />
                    </div>
                ) : (
                    <ListSelectorTable
                        flatRows={this.state.flatRows}
                        columns={columns}
                        expandedRows={this.state.expandedRows}
                        onCellClick={this.onCellClick.bind(this)}
                    />
                )}
            </div>
        );
    }
}

function mapStateToProps(state) {
    console.log('States in CreateProofModal:', state);
    const { s3Info } = state;
    return {
        s3TempInfo: s3Info.s3Info
    };
}

const connectedCreateProofModel = connect(mapStateToProps)(ListSelector);
export { connectedCreateProofModel as ListSelector };
